import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignFormDialog } from '@/components/campaigns/CampaignFormDialog';
import { Sparkline } from '@/components/charts/Sparkline';
import { api } from '@/lib/api';
import { statsApi } from '@/lib/api/stats';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import { formatCount, formatPercent } from '@/lib/formatters';
import { SOURCES } from '@/lib/types';
import type {
  AppRecord,
  Campaign,
  CampaignSource,
  CampaignStatus,
} from '@/lib/types';

type StatusFilter = CampaignStatus | 'all';
type SourceFilter = CampaignSource | 'all';
type AppFilter = number | 'all';

async function fetchCampaigns(status: StatusFilter): Promise<Campaign[]> {
  const { data } = await api.get<Campaign[]>('/v1/campaigns', {
    params: { status },
  });
  return data;
}

async function fetchApps(): Promise<AppRecord[]> {
  const { data } = await api.get<AppRecord[]>('/v1/apps');
  return data;
}

const STATUS_VARIANT: Record<
  CampaignStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  active: 'default',
  draft: 'outline',
  paused: 'secondary',
  archived: 'outline',
};

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows: Campaign[], appNameById: Map<number, string>) {
  const header = [
    'name',
    'code',
    'app',
    'type',
    'source',
    'status',
    'clicks',
    'installs',
    'cr_percent',
    'created_at',
  ];
  const body = rows.map((c) => {
    const clicks = c.clicks ?? 0;
    const installs = c.installs ?? 0;
    const cr = clicks > 0 ? ((installs / clicks) * 100).toFixed(2) : '';
    return [
      c.name,
      c.code,
      appNameById.get(c.appId) ?? c.appId,
      c.type,
      c.source,
      c.status,
      clicks,
      installs,
      cr,
      c.createdAt,
    ];
  });
  const csv =
    [header, ...body].map((row) => row.map(csvEscape).join(',')).join('\n') +
    '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Campaigns() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [appFilter, setAppFilter] = useState<AppFilter>('all');
  const [open, setOpen] = useState(false);

  const campaigns = useQuery({
    queryKey: queryKeys.campaigns(statusFilter),
    queryFn: () => fetchCampaigns(statusFilter),
  });
  const apps = useQuery({ queryKey: queryKeys.apps, queryFn: fetchApps });
  const sparklines = useQuery({
    queryKey: queryKeys.campaignSparklines(statusFilter, 7),
    queryFn: () => statsApi.campaignSparklines(7, statusFilter),
  });

  const sparkByCode = useMemo(
    () => new Map((sparklines.data ?? []).map((s) => [s.code, s])),
    [sparklines.data],
  );

  const archive = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/campaigns/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Campaign archived' });
    },
    onError: () =>
      toast({ title: 'Could not archive campaign', variant: 'destructive' }),
  });

  const appNameById = useMemo(
    () => new Map((apps.data ?? []).map((a) => [a.id, a.name])),
    [apps.data],
  );

  // Source + app filters are client-side; status comes from the API to keep
  // the network payload small.
  const filteredRows = useMemo(() => {
    const rows = campaigns.data ?? [];
    return rows.filter((c) => {
      if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
      if (appFilter !== 'all' && c.appId !== appFilter) return false;
      return true;
    });
  }, [campaigns.data, sourceFilter, appFilter]);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Each campaign has an opaque tracking code; paste the URL into your ads, emails, or QR codes.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={apps.data?.length === 0}>
          <Plus />
          New Campaign
        </Button>
      </header>

      {apps.data?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Register an app first — campaigns need an app to promote.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-40" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sourceFilter}
          onValueChange={(v) => setSourceFilter(v as SourceFilter)}
        >
          <SelectTrigger className="w-44" aria-label="Filter by source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={String(appFilter)}
          onValueChange={(v) => setAppFilter(v === 'all' ? 'all' : Number(v))}
        >
          <SelectTrigger className="w-48" aria-label="Filter by app">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            <SelectItem value="all">All apps</SelectItem>
            {(apps.data ?? []).map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            disabled={filteredRows.length === 0}
            onClick={() => downloadCsv(filteredRows, appNameById)}
          >
            <Download />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>App</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">Installs</TableHead>
              <TableHead className="text-right">CR</TableHead>
              <TableHead className="w-20">7-day</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.isLoading &&
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={11}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {campaigns.isError && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-destructive">
                  Could not load campaigns.
                </TableCell>
              </TableRow>
            )}
            {!campaigns.isLoading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground">
                  No campaigns match these filters.
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Link
                    to={`/campaigns/${c.id}`}
                    className="font-medium hover:underline"
                  >
                    {c.name}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell>{appNameById.get(c.appId) ?? c.appId}</TableCell>
                <TableCell>
                  <Badge variant={c.type === 'redirect' ? 'default' : 'secondary'}>
                    {c.type}
                  </Badge>
                </TableCell>
                <TableCell className="capitalize">
                  {c.source.replace('_', ' ')}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={STATUS_VARIANT[c.status]}
                    className="capitalize"
                  >
                    {c.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCount(c.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCount(c.installs)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatPercent(c.installs, c.clicks)}
                </TableCell>
                <TableCell>
                  <Sparkline
                    data={
                      c.type === 'landing'
                        ? sparkByCode.get(c.code)?.landingClicks ?? []
                        : sparkByCode.get(c.code)?.clicks ?? []
                    }
                    ariaLabel={`Last 7 days of activity for ${c.name}`}
                  />
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          navigator.clipboard.writeText(
                            `${import.meta.env.VITE_INSTALL_BASE_URL}/install?c=${c.code}`,
                          );
                          toast({ title: 'Tracking URL copied' });
                        }}
                      >
                        Copy tracking URL
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={c.status === 'archived' || archive.isPending}
                        onSelect={() => archive.mutate(c.id)}
                      >
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CampaignFormDialog
        open={open}
        onOpenChange={setOpen}
        apps={apps.data ?? []}
      />
    </div>
  );
}
